import { useForm, Controller } from "react-hook-form";
import { set, z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import DefaultLayout from "../layouts/default/Default.layout";
import DatePicker, { registerLocale } from "react-datepicker";
import { ptBR, se } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import "../../datepicker.css";
import { useState, useMemo, Fragment, useEffect, useCallback } from "react";
import { Button, Modal, Input, Select, Spin, notification } from "antd";
import { TriangleAlert, RotateCcw } from "lucide-react";
import { Column, useTable } from "react-table";
import Table from "../components/Table/Table";
import InscritosPanel from "../components/SimuladoInscritos/InscritosPanel";
import { Questao, Simulado, Banca, Materia } from "../../sdk/@types";
import { TagDto } from "../../sdk/@types/TagDto";
import {useSimulado} from "../../core/hooks/useSimulado";
import { useLocation, useParams } from "react-router-dom";
import useQuestao from "../../core/hooks/useQuestao";
import { ChoiceButton } from "../components/ChoiceButton/ChoiceButton";
import useQuestaoSimulado from "../../core/hooks/useQuestaoSimulado";
import QuestaoService from "../../sdk/services/QuestaoService";

const schema = z.object({
  id: z.string().optional(),
  description: z.string().min(6, "Informe pelo menos 6 caracteres"),
  start: z.date(),
  end: z.date(),
  type: z.enum(["competitivo", "periodico"], {
    message: "Selecione o tipo de simulado",
  }),
  situation: z.string().optional(),
  elapsed: z.string().optional(),
});

const schemaFilter = z.object({
  statement: z.string().optional(),
  start: z.date().optional(),
  end: z.date().optional(),
  testingAgencyIds: z.array(z.string()).optional(),
  subjectIds: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
});

type FormData = z.infer<typeof schema>;
type FormFilterData = z.infer<typeof schemaFilter>;

registerLocale("ptBR", ptBR);

export default function CadastroSimuladoForm() {
  const { simulado, simuladoOutcome, setSimulado, insertSimulado, updateSimulado, changeSituation, newSimulado, fetchSimulado } = useSimulado();
  const { questoes, setQuestoes, fetchQuestoesPorFiltro } = useQuestao();
  const { questoesSimulado, setQuestoesSimulado, insertMany, fetchQuestoesSimulado, deleteQuestaoSimulado } = useQuestaoSimulado();
  const [ selectedQuestions, setSelectedQuestions ] = useState<Questao.Questao[]>([]);
  const [active, setActive] = useState("Questões");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bancas, setBancas] = useState<Banca.Banca[]>([]);
  const [materias, setMaterias] = useState<Materia.Materia[]>([]);
  const [tagsOptions, setTagsOptions] = useState<TagDto.TagDto[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [filtering, setFiltering] = useState(false);
  const location = useLocation();
  const { id: idFromUrl } = useParams<{ id?: string }>();
  //const simuladoFromState = (location.state as { simulado: Simulado.Simulado } | undefined)?.simulado;
  const [ simuladoFromState, setSimuladoFromState] = useState<Simulado.Simulado | undefined>((location.state as { simulado: Simulado.Simulado } | undefined)?.simulado);
  // Só o ramo `idFromUrl` do efeito abaixo escreve aqui — as rotas sem id (simulado novo
  // ou vindo de location.state) nunca saem de 'idle', então nunca mostram a mensagem de
  // falha nem o indicador de carregando (critério 4).
  const [simuladoLoadStatus, setSimuladoLoadStatus] = useState<"idle" | "loading" | "failed">("idle");

  const columns: Column<Simulado.QuestaoSimulado>[] = useMemo(
    () => [
      {
        Header: "Questão",
        accessor: "question",
        Cell:({value}) => value?<p className="p-2">{value.statement}</p>:'não encontrado',
      },
      {
        id: 'question-subject',
        Header: "Matéria",
        accessor: "question",
        Cell:({value}) => value?<p className="w-[10vw]">{value.subject?.name}</p>:'não encontrado',
      },
      {
        Header: "Ação",
        Cell: ({ row }) => {
          const { situation } = row.original.mocktest || {};
          const disabled = ["finalizado", "disponivel", "cancelado"].includes(situation!);

          return (
            <Button
              disabled={disabled}
              onClick={() => handleDeleteQuestion(row.original)}
            >
              Remover
            </Button>
          );
        }        
      },
    ],
    []
  );

  const instance = useTable<Simulado.QuestaoSimulado>({
    columns,
    data: questoesSimulado??[],
  });

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      description: simulado.description ?? "",
      start: simulado.start ? new Date(simulado.start) : new Date(),
      end: simulado.end ? new Date(simulado.end) : new Date(),
      type: simulado.type ?? "competitivo",
      situation: simulado.situation ?? "edicao",
      elapsed: simulado.elapsed_time ?? "00:00:00",
    },
  });

  const {
    control: controlFilter,
    register: registerFilter,
    handleSubmit: handleSubmitFilter,
    watch: watchFilter,
    reset: resetFilter,
    formState: { errors: errorsFilter, isSubmitting: isSubmittingFilter },
  } = useForm<FormFilterData>({
    resolver: zodResolver(schemaFilter),
  });

  const onFilterSubmit = async (data: FormFilterData) => {
    const query: Questao.QuestionFilterByQuery = {
      statement: data.statement || undefined,
      start: data.start ? data.start.toISOString() : undefined,
      end: data.end ? data.end.toISOString() : undefined,
      testingAgencyIds: data.testingAgencyIds?.length ? data.testingAgencyIds : undefined,
      subjectIds: data.subjectIds?.length ? data.subjectIds : undefined,
      tags: data.tags?.length ? data.tags : undefined,
    };

    setFiltering(true);
    try {
      await fetchQuestoesPorFiltro(query);
    } catch {
      notification.error({ message: "Erro ao buscar questões", description: "Verifique os filtros e tente novamente." });
    } finally {
      setFiltering(false);
    }
  }

  const onSubmit = async (data: FormData) => {
    // Leitura falhada nunca grava `id` no formulário (o reset do efeito [simulado] nunca
    // roda) — sem esta guarda o envio cairia no ramo de criação e duplicaria o simulado.
    if (simuladoOutcome.status === 'failed') return;

    const newData : Simulado.Simulado = {
      id: data.id,
      description: data.description,
      start: data.start.toISOString(),
      end: data.end.toISOString(),
      type: data.type,
      situation: 'edicao',
    };

    if (data.id && data.id !== ''){
      await updateSimulado(newData);
      setSimuladoFromState(simulado);
    }else
      await insertSimulado(newData);


  };

  const handlePublish = async () => {
    const data : any = {
      id: simulado.id,
      situation: 'disponivel'
    }
    await changeSituation(data);
  }

  const handleUnPublish = async () => {
    const data : any = {
      id: simulado.id,
      situation: 'edicao'
    }
    await changeSituation(data);
  }

  const situation : Record<string, JSX.Element> = {
    disponivel: <p className="text-green-400 font-bold text-2xl">Disponível Para Inscrição</p>,
    cancelado: <p className="text-red-400 font-bold text-2xl">Cancelado</p>,
    finalizado: <p className="text-gray-400 font-bold text-2xl">Finalizado</p>,
    edicao: <p className="text-yellow-400 font-bold text-2xl">Edição</p>,
    novo: <p className="text-blue-400 font-bold text-2xl">Novo Simulado</p>,
  };

  const showModal = async () => {
    setIsModalOpen(true);
    if (bancas.length === 0 || materias.length === 0) {
      setLoadingOptions(true);
      try {
        const [b, m, t] = await Promise.all([
          QuestaoService.getTestingAgencies(),
          QuestaoService.getSubjects(),
          QuestaoService.buscarTagsDasPalavrasChaves(),
        ]);
        setBancas(b ?? []);
        setMaterias(m ?? []);
        setTagsOptions(t ?? []);
      } catch {
        notification.error({ message: "Erro ao carregar opções de filtro" });
      } finally {
        setLoadingOptions(false);
      }
    }
  };

  const handleOk = () => {
    setIsModalOpen(false);
    handleIncludeQuestions();
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  }; 

  // verify if the question is already selected
  const isQuestionSelected = (questaoId: number) => {
    return selectedQuestions?.some((q) => q.id === questaoId);
  };

  // verify if the question is already persisted
  const isQuestionPersisted = (questaoId: number) => {
    return questoesSimulado?.some((q) => q.question?.id === questaoId);
  }

  // function to handle question selection
  const handleQuestionSelect = (questao: Questao.Questao) => {
    if (isQuestionSelected(questao.id!)) {
      setSelectedQuestions(selectedQuestions.filter((q) => q.id !== questao.id));
    }
    else {
      setSelectedQuestions([...selectedQuestions, questao]);
    }
  };

  // function to handle select all questions
  const handleSelectAllQuestions = () => {
    if (selectedQuestions.length === questoes.length) { 
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(questoes);
    }
  };

  const handleClearFilter = () => {
    setQuestoes([]);
    setSelectedQuestions([]);
    resetFilter();
  };

  // delete question from simulado
  const handleDeleteQuestion = async (questao: Simulado.QuestaoSimulado) =>{

    if (!questao.mock_test_id || !questao.id)
      return;

    await deleteQuestaoSimulado(questao.id?.toString());
    fetchQuestoesSimulado(questao.mock_test_id!)
  }

  // Include all selected questions in the simulado
  const handleIncludeQuestions = () => {
    if (!simulado.id) return;

    const questionsSelected : Simulado.QuestaoSimulado[] = selectedQuestions?.map((questao) => ({
      mock_test_id: simulado.id,
      question_id: questao.id!.toString(),
    }));

    const mockTest : Simulado.Simulado = {
      ...simulado,
      questions: [...questionsSelected],
    };

    setSelectedQuestions([]);
    insertMany(mockTest);
  };

  // GET /mocktests/:id ainda não existe no Go (só /mocktests e /mocktests/allbyuser) — 404 até
  // landar lá. Por isso esta leitura falha hoje, e é exatamente o estado que este bloco passa a
  // sinalizar em vez de deixar a rejeição silenciosa: carregando -> falhou (sem id nunca entra no
  // formulário) ou carregando -> sucesso. "Tentar de novo" chama a mesma função.
  const loadSimuladoFromUrl = useCallback(
    (id: string) => {
      setSimuladoLoadStatus("loading");
      fetchSimulado(id)
        .then(() => setSimuladoLoadStatus("idle"))
        .catch(() => setSimuladoLoadStatus("failed"));
    },
    [fetchSimulado]
  );

  // Optionally update form dynamically when simulado changes
  useEffect(() => {
    if (idFromUrl) {
      loadSimuladoFromUrl(idFromUrl);
    } else if (simuladoFromState) {
      // MyRoutes.tsx aponta /cadastro-simulado e /editar-simulado para este mesmo componente;
      // trocar de um /editar-simulado/:id (possivelmente 'failed'/'loading') para uma dessas
      // rotas não remonta — React Router reaproveita a mesma instância — então sem este reset
      // a tela de uma nova inscrição nasceria mostrando a falha de carregamento da anterior.
      setSimuladoLoadStatus("idle");
      setSimulado(simuladoFromState);

      reset({
        id: simuladoFromState.id ?? "",
        description: simuladoFromState.description ?? "",
        start: simuladoFromState.start ? new Date(simuladoFromState.start) : new Date(),
        end: simuladoFromState.end ? new Date(simuladoFromState.end) : new Date(),
        type: simuladoFromState.type ?? "competitivo",
        situation: simuladoFromState.situation ?? "novo",
        elapsed: simuladoFromState.elapsed_time ?? "00:00:00",
      });

      fetchQuestoesSimulado(simuladoFromState.id!);
    } else {
      // Mesmo motivo do reset acima: sem id e sem location.state também é alcançado sem
      // remontar (ex.: "Simulado › Novo" do Sidebar), então precisa do mesmo reset.
      setSimuladoLoadStatus("idle");
      newSimulado();
      reset({
        id: "",
        description: "",
        start: new Date(),
        end: new Date(),
        type: "periodico",
        situation: "novo",
        elapsed: "00:00:00",
      });
      setQuestoesSimulado([]);
    }
  }, [idFromUrl, reset, setSimulado, newSimulado, simuladoFromState, loadSimuladoFromUrl]);

  useEffect(() => {
    if (simulado) {

      reset({
        id: simulado.id ?? "",
        description: simulado.description ?? "",
        start: simulado.start ? new Date(simulado.start) : new Date(),
        end: simulado.end ? new Date(simulado.end) : new Date(),
        type: simulado.type ?? "competitivo",
        situation: simulado.situation ?? "novo",
        elapsed: simulado.elapsed_time ?? "00:00:00",
      });

      fetchQuestoesSimulado(simulado.id!);
    }

  }, [simulado]);

  const handleRetrySimuladoLoad = () => {
    if (idFromUrl) loadSimuladoFromUrl(idFromUrl);
  };

  const handleDisableStyle = () : String => {
    return `${simulado.situation === 'finalizado' || simulado.situation === 'disponivel' || simulado.situation === 'cancelado'?'grayscale pointer-events-none select-none select-none focus:outline-none':''}`;
  }

  const handleDisable = () : boolean => {
    return (simulado.situation === 'finalizado' || simulado.situation === 'disponivel' || simulado.situation === 'cancelado');
  }

  // Só a gravação sai do ar numa leitura falhada — Publicar/Revogar ficam fora desta
  // guarda de propósito, porque publicar não cria um simulado novo.
  const handleSaveDisable = () : boolean => {
    return handleDisable() || simuladoOutcome.status === 'failed';
  }

  return (
    <DefaultLayout>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="max-w-[90%] mx-auto p-4 bg-white dark:bg-gray-900 shadow-md rounded-2xl flex flex-col gap-4 w-full sm:w-[90%]"
      >
        <div className="justify-start mb-2 border-b dark:border-slate-700 p-2">
          <p className="text-2xl font-bold text-gray-500 dark:text-gray-300">Simulado</p>
          <p className="text-1x1 font-normal text-gray-500 dark:text-gray-300">
            Manutenção do cadastro de simulado
          </p>
        </div>
        <div className={`flex flex-row gap-3 w-full ${handleDisableStyle()}`}>
          <div className="w-[50%]">
            <label className="block text-sm font-medium dark:text-gray-200">Descrição</label>
            <input
              type="text"
              {...register("description")}
              disabled={handleDisable()}
              className="w-full border rounded-md px-3 py-2 mt-1 focus:outline-none focus:ring focus:ring-blue-300 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-slate-600"
            />
          </div>
          <div className="w-full sm:w-[25%]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Início
            </label>

            <Controller
              name="start"
              control={control}
              render={({ field }) => (
                <DatePicker
                  selected={field.value}
                  onChange={field.onChange}
                  disabled={handleDisable()}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="dd/MM/yyyy HH:mm"
                  locale="ptBR"
                  placeholderText="Selecione data e hora"
                  showPopperArrow={false}
                  className="!w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors cursor-pointer bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  calendarClassName="!rounded-xl !shadow-lg !border-gray-200 w-full"
                  popperClassName="!z-50"
                  wrapperClassName="w-full"
                />
              )}
            />
          </div>

          <div className="w-full sm:w-[25%]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Fim
            </label>

            <Controller
              name="end"
              control={control}
              render={({ field }) => (
                <DatePicker
                  selected={field.value}
                  onChange={field.onChange}
                  disabled={handleDisable()}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="dd/MM/yyyy HH:mm"
                  locale="ptBR"
                  placeholderText="Selecione data e hora"
                  showPopperArrow={false}
                  className="!w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors cursor-pointer bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  calendarClassName="!rounded-xl !shadow-lg !border-gray-200 w-full"
                  popperClassName="!z-50"
                  wrapperClassName="w-full"
                />
              )}
            />
          </div>

        </div>
        <div className="flex flex-row w-full items-center justify-between gap-4">

          <div className={`flex space-x-2 ${handleDisableStyle()}}`}>
            {["competitivo", "periodico"].map((option) => (
              <label
                key={option}
                className={`
                cursor-pointer px-4 py-2 rounded-md text-white font-medium
                transition-colors
                ${
                  watch("type") === option ? "bg-blue-600" : "bg-gray-400"
                }
                `}
                    >
                      <input
                        type="radio"
                        value={option}
                        {...register("type")}
                        className="sr-only"
                        />
                      {option}
                    </label>
                  ))}
          </div>
          <div className="mt-1 w-full justify-end text-right mr-10 flex flex-row gap-3">
            {watch("situation") ? <div className="flex flex-row"><p className="text-gray-800 dark:text-gray-100 font-bold text-2xl mr-4">Situação:</p>{situation[watch("situation") as keyof typeof situation]}</div> : ''}
          </div>
        </div>
        {errors.description && (
          <p className="text-red-500 text-sm">{errors.description.message}</p>
        )}

        <div>
          {simuladoLoadStatus === "loading" ? (
            <div role="status" className="flex items-center justify-center gap-3 rounded-card border border-edge bg-surface py-16">
              <Spin />
              <span className="text-sm text-ink-secondary">Carregando simulado…</span>
            </div>
          ) : simuladoLoadStatus === "failed" ? (
            <div className="flex flex-col items-center gap-3 rounded-card border border-warn/40 bg-warn/[0.08] px-6 py-10 text-center">
              <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-card border border-warn/40 bg-warn/[0.12] text-warn-ink">
                <TriangleAlert size={20} aria-hidden="true" />
              </span>
              <div className="max-w-md">
                <p className="text-base font-semibold text-warn-ink">Não foi possível carregar este simulado</p>
                <p className="mt-1 text-sm text-ink-secondary">
                  Tente de novo — o restante do produto continua normal.
                </p>
              </div>
              <button
                type="button"
                onClick={handleRetrySimuladoLoad}
                className="mt-1 inline-flex h-10 items-center gap-2 rounded-input border border-edge bg-surface px-4 text-sm font-medium text-ink-secondary transition-colors hover:border-edge-strong hover:bg-surface-hover hover:text-ink"
              >
                <RotateCcw size={15} aria-hidden="true" />
                Tentar de novo
              </button>
            </div>
          ) : (
            <div className={`w-full ${!simulado.id ? 'grayscale pointer-events-none select-none' : ''}`}>
              <div className="flex border-b border-gray-200 dark:border-slate-700">
                {["Questões", "Inscritos"].map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActive(tab)}
                    className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${
                      active === tab
                      ? "border-b-2 border-blue-500 text-blue-600"
                      : "text-gray-500 dark:text-gray-400 hover:text-blue-500"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              <div className="mt-4">
                {active === "Questões" && (
                  <div className={`${handleDisableStyle()}`}>
                    <div className="flex justify-end p-3 w-full">
                      <Button type="primary" disabled={handleDisable()} onClick={showModal}>Incluir</Button>
                    </div>
                    <Table instance={instance} />
                  </div>
                )}
                {active === "Inscritos" && simulado.id && (
                  <InscritosPanel mocktestId={simulado.id} mocktestName={simulado.description ?? ""} />
                )}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 dark:border-slate-700 p-3 flex justify-end w-full gap-2">
          <Button type="primary" htmlType="submit" disabled={handleSaveDisable()} >
            {simulado.id?'Atualizar':'Salvar'}
          </Button>

          {simulado.situation === 'disponivel'?(
            <Button type="primary" danger onClick={handleUnPublish}  >
              Revogar Publicação
            </Button>
          ):(
            <Button type="primary" danger onClick={handlePublish} disabled={simulado.situation === 'finalizado'||simulado.situation === undefined} >
              Publicar
            </Button>
          )}

        </div>
      </form>

      <Modal
        title="Incluir Questões"
        closable={{ 'aria-label': 'Custom Close Button' }}
        open={isModalOpen}
        onOk={handleOk}
        okText="Incluir"
        width={860}
        onCancel={handleCancel}
      >
        <Spin spinning={filtering} tip="Buscando questões...">
        <form onSubmit={handleSubmitFilter(onFilterSubmit)}>
          {/* ── Filter section ── */}
          <div className="px-1 pt-2 pb-3 border-b border-gray-200 dark:border-slate-700 flex flex-col gap-3">

            {/* Enunciado — plain text, no markdown */}
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Enunciado</p>
              <Controller
                control={controlFilter}
                name="statement"
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Buscar por trecho do enunciado..."
                    allowClear
                  />
                )}
              />
            </div>

            {/* Bancas + Matérias — 2 columns */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Bancas</p>
                <Controller
                  control={controlFilter}
                  name="testingAgencyIds"
                  render={({ field }) => (
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder="Selecione as bancas"
                      loading={loadingOptions}
                      className="w-full"
                      value={field.value ?? []}
                      onChange={field.onChange}
                      maxTagCount="responsive"
                      options={bancas.map((b) => ({ value: b.id!, label: b.name && b.year ? `${b.name}-${b.year}` : (b.name ?? b.id) }))}
                    />
                  )}
                />
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Matérias</p>
                <Controller
                  control={controlFilter}
                  name="subjectIds"
                  render={({ field }) => (
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder="Selecione as matérias"
                      loading={loadingOptions}
                      className="w-full"
                      value={field.value ?? []}
                      onChange={field.onChange}
                      maxTagCount="responsive"
                      options={materias.map((m) => ({ value: m.id!, label: m.name ?? m.id }))}
                    />
                  )}
                />
              </div>
            </div>

            {/* Tags — full width */}
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Tags</p>
              <Controller
                control={controlFilter}
                name="tags"
                render={({ field }) => (
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="Selecione as tags"
                    loading={loadingOptions}
                    className="w-full"
                    value={field.value ?? []}
                    onChange={field.onChange}
                    maxTagCount="responsive"
                    options={tagsOptions
                      .filter((t) => !!t.description)
                      .map((t) => ({ value: t.description!, label: t.description! }))}
                  />
                )}
              />
            </div>

            {/* Período de criação */}
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Período de criação</p>
              <div className="flex flex-row gap-3">
                <div className="w-full">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Início</p>
                  <Controller
                    name="start"
                    control={controlFilter}
                    render={({ field }) => (
                      <DatePicker
                        selected={field.value}
                        onChange={field.onChange}
                        dateFormat="dd/MM/yyyy"
                        locale="ptBR"
                        placeholderText="Selecione a data"
                        showPopperArrow={false}
                        className="!w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors cursor-pointer bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        calendarClassName="!rounded-xl !shadow-lg !border-gray-200 w-full"
                        popperClassName="!z-50"
                        wrapperClassName="w-full"
                      />
                    )}
                  />
                </div>
                <div className="w-full">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Fim</p>
                  <Controller
                    name="end"
                    control={controlFilter}
                    render={({ field }) => (
                      <DatePicker
                        selected={field.value}
                        onChange={field.onChange}
                        dateFormat="dd/MM/yyyy"
                        locale="ptBR"
                        placeholderText="Selecione a data"
                        showPopperArrow={false}
                        className="!w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors cursor-pointer bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        calendarClassName="!rounded-xl !shadow-lg !border-gray-200 w-full"
                        popperClassName="!z-50"
                        wrapperClassName="w-full"
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ── Action buttons ── */}
          <div className="flex flex-row items-center py-2 border-b border-gray-100 dark:border-slate-700">
            {questoes.length > 0 && (
              <div className="flex-1">
                <Button type="primary" ghost size="small" onClick={handleSelectAllQuestions}>
                  Selecionar todos ({questoes.length})
                </Button>
              </div>
            )}
            <div className="flex gap-2 ml-auto">
              <Button onClick={handleClearFilter}>Limpar Filtro</Button>
              <Button type="primary" htmlType="submit">Filtrar</Button>
            </div>
          </div>

          {/* ── Results ── */}
          <div className="gap-1 flex flex-col h-[40vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent mt-1">
            {questoes.length === 0 && (
              <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-400 text-sm">
                Use os filtros acima para buscar questões
              </div>
            )}
            {questoes.map((questao) => (
              <Fragment key={questao.id}>
                <ChoiceButton
                  label={questao.statement ?? ''}
                  situation={isQuestionSelected(questao.id!) ? 'selected' : 'default'}
                  onClick={() => handleQuestionSelect(questao)}
                  disabled={isQuestionPersisted(questao.id!)}
                />
              </Fragment>
            ))}
          </div>
        </form>
        </Spin>
      </Modal>      
    </DefaultLayout>
  );
}
